import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleChevronLeftIcon,
  DoubleChevronRightIcon,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  PlusIcon,
  ThematicIcon,
  Tooltip,
  TrashDeleteIcon,
  Typography,
} from '@acceso/design-system';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DurationInput } from '@/components/DurationInput/DurationInput';
import { TimeInput } from '@/components/TimeInput/TimeInput';
import { REVIEW_STATES } from '@/constants';
import { useBroadcastSchedulePermissions } from '@/features/broadcast-schedule/hooks/useBroadcastSchedulePermissions';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useNavigationKeywords } from '@/features/broadcast-schedule-program/hooks/useNavigationKeywords';
import { TSegment } from '@/features/segments/types';
import { formatDateCustom } from '@/helpers/format-date';
import { StatusEnrichmentEnum } from '@/types/types';

import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState } from '../hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState';
import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState } from '../hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState';

type TBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListProps = {
  items: TSegment[];
  programBroadcast: TProgramBroadcast;
  videoHasError: boolean;
  setSegmentItems: Dispatch<SetStateAction<TSegment[]>>;
  disableSegmentEditing?: boolean;
};

const animationClassMap = {
  'enter-left': 'animate-enter-left',
  'enter-right': 'animate-enter-right',
  'exit-left': 'animate-exit-left',
  'exit-right': 'animate-exit-right',
};

export function BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList({
  items,
  programBroadcast,
  videoHasError,
  setSegmentItems,
  disableSegmentEditing = false,
}: TBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListProps) {
  const { t } = useTranslation();
  const {
    handleNextItem,
    handlePreviousItem,
    handleGoXItem,
    handleGoFirst,
    handleGoLast,
    hasNextItem,
    hasPreviousItem,
  } = useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState({ data: items });
  const {
    segments,
    visibleItems,
    totalSecondsItems,
    currentSecondGlobal,
    currentAbsoluteTime,
    earliestTime,
    latestTime,
    sliderValue,
    detailCardRef,
    visibleStartIndex,
    selectedIndex,
    disableAddPreviousSegment,
    disableAddNextSegment,
    handleAddSegment,
    handleRemoveSegment,
    handleSliderChange,
    handleChangeTimeInput,
    handleChangeAbsoluteTimeInput,
    handleWheel,
  } = useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState({
    items,
    setSegmentItems,
  });
  useNavigationKeywords({
    handleClickNextUi: handleNextItem,
    handleClickPrevUi: handlePreviousItem,
    shortcutsDisabled: false,
  });

  const { activeSegment } = useBroadcastScheduleProgram();
  const { canUpdateInterval: canUpdateProgramBroadcastInterval } =
    useBroadcastSchedulePermissions();

  const hasEnrichmentSttError =
    activeSegment?.parameters?.status_enrichments.stt?.status === StatusEnrichmentEnum.ERROR;

  const isInStreaming = useMemo(() => {
    if (!programBroadcast?.start_datetime || !programBroadcast?.end_datetime) return false;

    const isInStreaming =
      new Date() >= new Date(programBroadcast?.start_datetime) &&
      new Date() <= new Date(programBroadcast?.end_datetime);

    return isInStreaming;
  }, [programBroadcast?.start_datetime, programBroadcast?.end_datetime]);

  return (
    <div ref={detailCardRef} className="flex w-full flex-col items-center justify-center gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-center justify-between gap-1">
          <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Semibold}>
            {currentSecondGlobal} / {totalSecondsItems}
          </Typography>
          <DurationInput
            initialValue={currentSecondGlobal}
            onConfirm={formattedTime => {
              handleChangeTimeInput({
                target: { value: formattedTime },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />

          <TimeInput
            initialValue={currentAbsoluteTime}
            minValue={earliestTime}
            maxValue={latestTime}
            onConfirm={formattedTime => {
              handleChangeAbsoluteTimeInput({
                target: { value: formattedTime },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        </div>
        <div className="flex gap-1">
          {hasPreviousItem && (
            <Tooltip grow help={t('Elemento inicial')}>
              <DoubleChevronLeftIcon className="cursor-pointer" onClick={handleGoFirst} />
            </Tooltip>
          )}
          {hasPreviousItem && (
            <Tooltip grow help={t('Elemento anterior')}>
              <ChevronLeftIcon className="cursor-pointer" onClick={handlePreviousItem} />
            </Tooltip>
          )}
        </div>
        {!disableSegmentEditing &&
          canUpdateProgramBroadcastInterval &&
          !disableAddPreviousSegment && (
            <Tooltip grow help={t('Añadir una franja anterior')}>
              <span
                className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-neutral-50 text-accent-neutral-00"
                onClick={() => handleAddSegment('previous')}
              >
                <PlusIcon size={IconSizeEnum.SM} />
              </span>
            </Tooltip>
          )}
        {!disableSegmentEditing && canUpdateProgramBroadcastInterval && segments.length > 1 && (
          <Tooltip grow help={t('Eliminar primera franja')}>
            <span
              className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-extra-25 text-extra-20"
              onClick={() => handleRemoveSegment('first')}
            >
              <TrashDeleteIcon size={IconSizeEnum.SM} />
            </span>
          </Tooltip>
        )}
        {visibleItems.map((item, index) => {
          const globalIndex = visibleStartIndex + index;
          return (
            <Tooltip
              help={
                <div className="flex gap-1">
                  {formatDateCustom({ input: item.start_datetime, format: 'HH:mm:ss' })}
                  <span>-</span>
                  {formatDateCustom({ input: item.end_datetime, format: 'HH:mm:ss' })}
                </div>
              }
              grow
            >
              <span
                key={item.id}
                onClick={() => handleGoXItem(globalIndex)}
                className={`flex h-[40px] w-[40px] cursor-pointer items-center justify-center ${REVIEW_STATES.find(review => review.value === item.review_state)?.class} ${
                  item.animation ? animationClassMap[item.animation] : ''
                } ${globalIndex === selectedIndex ? 'border-3 !border-accent-active-primary-40' : ''}`}
              >
                {globalIndex}
              </span>
            </Tooltip>
          );
        })}
        {videoHasError && (
          <Tooltip grow help={t('Error de vídeo')}>
            <span className="flex h-[30px] w-[30px] items-center justify-center border border-accent-active-primary-30 text-accent-static-medium-dark">
              <PlusIcon size={IconSizeEnum.SM} />
            </span>
          </Tooltip>
        )}
        {hasEnrichmentSttError && (
          <Tooltip grow help={t('Error de transcripción')}>
            <span className="flex h-[30px] w-[30px] items-center justify-center border border-accent-active-primary-30 text-accent-static-medium-dark">
              <ThematicIcon size={IconSizeEnum.SM} />
            </span>
          </Tooltip>
        )}
        {!disableSegmentEditing &&
          canUpdateProgramBroadcastInterval &&
          !disableAddNextSegment &&
          !isInStreaming && (
            <Tooltip grow help={t('Añadir una franja posterior')}>
              <span
                className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-neutral-50 text-accent-neutral-00"
                onClick={() => handleAddSegment('next')}
              >
                <PlusIcon size={IconSizeEnum.SM} />
              </span>
            </Tooltip>
          )}
        {!disableSegmentEditing && canUpdateProgramBroadcastInterval && segments.length > 1 && (
          <Tooltip grow help={t('Eliminar última franja')}>
            <span
              className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-extra-25 text-extra-20"
              onClick={() => handleRemoveSegment('last')}
            >
              <TrashDeleteIcon size={IconSizeEnum.SM} />
            </span>
          </Tooltip>
        )}
        <div className="flex gap-1">
          {hasNextItem && (
            <Tooltip grow help={t('Elemento posterior')}>
              <ChevronRightIcon className="cursor-pointer" onClick={handleNextItem} />
            </Tooltip>
          )}
          {hasNextItem && (
            <Tooltip grow help={t('Elemento final')}>
              <DoubleChevronRightIcon className="cursor-pointer" onClick={handleGoLast} />
            </Tooltip>
          )}
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step="0.01"
        value={sliderValue}
        onChange={handleSliderChange}
        onWheel={handleWheel}
        className="!hover:accent-accent-active-primary-40 w-[60%] !accent-accent-active-primary-40"
      />
    </div>
  );
}
